import { useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useAuthStore } from 'lib/stores/authStore';
import { useTickerQuotesStore, type LifecycleStatus, type QuoteData } from 'lib/stores/tickerQuotesStore';

import { BackgroundWorkContext } from './backgroundWorkContext';

export interface LiveMarketData {
    quote: QuoteData | undefined;
    status: LifecycleStatus | undefined;
}

const EMPTY_RESULT: Record<string, LiveMarketData> = {};
const EMPTY_FLAT: Array<QuoteData | LifecycleStatus | undefined> = [];

/**
 * How long values held from a pause keep standing in for the store after work resumes —
 * quotes and lifecycle statuses alike.
 *
 * The hold exists for one job: cover the gap while a released subscription is reacquired,
 * so covered cards don't visibly drop to the older REST values for a few frames. It is
 * bounded uniformly because NOTHING the WebSocket told us stays authoritative once we stop
 * listening. `market_lifecycle_v2` is a global broadcast with no per-ticker snapshot
 * (`refreshTickerSnapshot` skips it by design), so any change that happens while a ticker
 * is unwatched — a determination, an amendment or dispute of one (`marketStatuses` carries
 * both), a settlement — is dropped and never resent. A held value that outlives this window
 * has no way to learn it is wrong, and it outranks REST: `applyLiveMarketDataToEntity`
 * prefers `status?.result`, and `deriveMarketStatus(live) ?? snapshot.status` never falls
 * through for a defined live status.
 *
 * REST is the opposite: it resumes polling at the same moment, carries result /
 * determination / settlement itself, and corrects itself on the next poll. So the failure
 * mode of expiring is transient and self-healing (a settled card may show its pre-pause
 * REST lifecycle until the next poll lands), while the failure mode of holding is permanent
 * (a superseded winning outcome displayed indefinitely). On a real-money surface, transient
 * beats permanent.
 */
const PAUSED_SNAPSHOT_HOLD_MS = 10_000;

/**
 * Read-side hook that exposes live ticker quotes + lifecycle status from
 * `tickerQuotesStore` for a set of tickers, and ref-counted-subscribes to each
 * via the store's watcher.
 *
 * Internally memoizes by content key (sorted-joined) so callers can pass a
 * fresh array every render without thrashing the watch/unwatch effect or the
 * zustand selector. The final result map is keyed by ticker, so input order
 * is not observable.
 *
 * Gated on `isLoggedIn`, not `isLoggedInMember`: applicants and `fcm_member`
 * accounts are logged-in but not in `MEMBER_ACCESS_LEVELS`, and grid surfaces
 * (`MarketGridClient`, etc.) disable fallback polling for any logged-in user.
 * Tile data would stale out for the entire session if we gated on member here.
 *
 * Logged-out callers skip both the subscription and the store read so
 * Storybook / public renders stay on their static fixture data, and stale
 * member-session data still cached in the store after a sign-out-without-refresh
 * can't leak into anonymous views.
 */
export function useLiveMarketQuotes(tickers: readonly string[]): Record<string, LiveMarketData> {
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
    const backgroundActive = useContext(BackgroundWorkContext);

    // Content key — stable when the SET of tickers hasn't changed, regardless
    // of caller-side reference identity or input order. Memoized on the array's
    // identity so callers that hold a stable reference (`useLiveEventMarkets`
    // with a big event) skip the O(n log n) sort+join on renders they didn't
    // cause; fresh-array callers just recompute to the same key, exactly as
    // before.
    const tickersKey = useMemo(() => [...tickers].sort().join('|'), [tickers]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableTickers = useMemo(() => tickers, [tickersKey]);

    // Keyed by ticker rather than by position: `stableTickers` preserves caller
    // order, which two callers can differ on for the same (order-insensitive)
    // `tickersKey`, so an index-aligned snapshot could pair one ticker's held
    // quote with another's.
    const [pausedQuotes, setPausedQuotes] = useState<{
        key: string;
        quotes: Record<string, QuoteData | undefined>;
        statuses: Record<string, LifecycleStatus | undefined>;
    } | null>(null);

    // Flat tuple of [quote, status, quote, status, ...] keeps `useShallow`
    // doing item-by-item reference comparison. The store keeps the outer
    // `quotes` / `statuses` records stable and replaces only changed ticker
    // entries, so unrelated WS ticks do not re-render this consumer.
    const flat = useTickerQuotesStore(
        useShallow((s) => {
            // Short-circuit logged-out reads so the selector skips the O(2n)
            // iteration on every store update for tiles that won't surface
            // live data anyway.
            if (!isLoggedIn) return EMPTY_FLAT;
            const held = pausedQuotes?.key === tickersKey ? pausedQuotes : undefined;
            const out: Array<QuoteData | LifecycleStatus | undefined> = [];
            for (const ticker of stableTickers) {
                // Frozen while paused: another, foreground consumer may keep this
                // ticker ticking, but a covered card shows what it had when it
                // released its watch.
                if (!backgroundActive && held) {
                    out.push(held.quotes[ticker], held.statuses[ticker]);
                    continue;
                }
                // Held per ticker through reacquisition after resume. When these
                // cards were the ticker's last watchers, pausing dropped the cached
                // quote with the watch, so the store reads empty until the resumed
                // WS snapshot lands — reading straight through would visibly fall
                // back to the older BFF price for those frames.
                out.push(s.quotes[ticker] ?? held?.quotes[ticker], s.statuses[ticker] ?? held?.statuses[ticker]);
            }
            return out;
        }),
    );
    // Mirror of the last values rendered from live data. Only references are stored, so
    // recording a tick is O(1) and costs no re-render — `flat` is replaced, never mutated.
    //
    // The pause snapshot has to come from here rather than from `flat` at capture time:
    // `unwatch` drops the store entries on a microtask, and whether that lands before or
    // after this commit's effects flush is not something the hook controls. If it lands
    // first, `flat` has already been recomputed to nothing and there is no longer anything
    // to snapshot.
    const lastLiveRef = useRef<{
        key: string;
        tickers: readonly string[];
        flat: Array<QuoteData | LifecycleStatus | undefined>;
    } | null>(null);
    useEffect(() => {
        if (!backgroundActive || !isLoggedIn) return;
        lastLiveRef.current = { key: tickersKey, tickers: stableTickers, flat };
    }, [backgroundActive, isLoggedIn, flat, tickersKey, stableTickers]);

    // Snapshot those values on pause/key changes, rather than setting state on every tick.
    const capturePausedQuotes = useEffectEvent(() => {
        const live = lastLiveRef.current;
        // Paused before any live data arrived for this ticker set — nothing to hold, so
        // reads fall through to the store exactly as they did before the pause.
        if (live === null || live.key !== tickersKey) {
            setPausedQuotes(null);
            return;
        }
        const quotes: Record<string, QuoteData | undefined> = {};
        const statuses: Record<string, LifecycleStatus | undefined> = {};
        for (let i = 0; i < live.tickers.length; i++) {
            quotes[live.tickers[i]] = live.flat[i * 2] as QuoteData | undefined;
            statuses[live.tickers[i]] = live.flat[i * 2 + 1] as LifecycleStatus | undefined;
        }
        setPausedQuotes({ key: tickersKey, quotes, statuses });
    });
    // Whether the held values have stopped covering for the store: every ticker they
    // carried a quote or a status for has one again (or the ticker set moved on), so they
    // can go. Quotes and statuses are tracked separately because the store can hold one
    // without the other — `onDisconnect` drops quotes but keeps statuses, so a ticker
    // paused during a WS outage has a held status and no held quote, and a quote-only
    // condition would discard its determined/settled status the instant work resumed.
    const pausedQuotesAreSpent = useTickerQuotesStore((s) => {
        if (pausedQuotes === null) return false;
        if (pausedQuotes.key !== tickersKey) return true;
        return stableTickers.every(
            (ticker) =>
                (pausedQuotes.quotes[ticker] === undefined || s.quotes[ticker] !== undefined) &&
                (pausedQuotes.statuses[ticker] === undefined || s.statuses[ticker] !== undefined),
        );
    });

    useEffect(() => {
        if (!backgroundActive) capturePausedQuotes();
    }, [backgroundActive, tickersKey]);

    useEffect(() => {
        if (!backgroundActive || pausedQuotes === null) return;
        if (pausedQuotesAreSpent) {
            setPausedQuotes(null);
            return;
        }
        // Normally the hold ends above, by reconciliation. This is the backstop for when the
        // reacquired subscription never delivers — see `PAUSED_SNAPSHOT_HOLD_MS` for why
        // nothing held, quote or status, is exempt from it.
        const timer = window.setTimeout(() => setPausedQuotes(null), PAUSED_SNAPSHOT_HOLD_MS);
        return () => window.clearTimeout(timer);
    }, [backgroundActive, pausedQuotes, pausedQuotesAreSpent]);

    const { watch, unwatch } = useTickerQuotesStore((s) => s.actions);

    useEffect(
        function manageWsSubscriptions() {
            if (!backgroundActive || !isLoggedIn || stableTickers.length === 0) return;
            for (const ticker of stableTickers) watch(ticker);
            return () => {
                for (const ticker of stableTickers) unwatch(ticker);
            };
        },
        [backgroundActive, isLoggedIn, stableTickers, watch, unwatch],
    );

    return useMemo(() => {
        if (!isLoggedIn || stableTickers.length === 0) return EMPTY_RESULT;
        const result: Record<string, LiveMarketData> = {};
        for (let i = 0; i < stableTickers.length; i++) {
            result[stableTickers[i]] = {
                quote: flat[i * 2] as QuoteData | undefined,
                status: flat[i * 2 + 1] as LifecycleStatus | undefined,
            };
        }
        return result;
    }, [isLoggedIn, stableTickers, flat]);
}

/**
 * Whether a market card's ticker subscription has delivered at least one quote snapshot.
 *
 * This deliberately observes the cache without acquiring another watch. The card that renders the
 * market owns the subscription; polling fallbacks can use this signal to stay active while that
 * subscription is pending, deferred, or otherwise has not produced usable data yet.
 */
export function useHasLiveMarketQuote(ticker: string): boolean {
    return useTickerQuotesStore((state) => !!ticker && state.quotes[ticker] !== undefined);
}

/**
 * Whether ANY of `tickers` has delivered a quote snapshot — the whole-event form of
 * `useHasLiveMarketQuote`, for surfaces whose fallback poll covers every market at once
 * (`ForecastEventWidget`). Same non-acquiring cache read: the rendering surface owns the
 * subscriptions; this only reports on them. O(n) per store write, but the boolean output means
 * subscribers re-render only when it flips.
 */
export function useHasAnyLiveMarketQuote(tickers: readonly string[]): boolean {
    return useTickerQuotesStore((state) => tickers.some((ticker) => state.quotes[ticker] !== undefined));
}
